import EVImg from '@components/EVImg/EVImg';
import { AspectBox } from '@components/Layout';
import { query } from '@design/templateFns';
import { Prediction, PredictionTypeEnum } from '@engelvoelkersamericas/api';
import { apiClient } from 'api-client';
import { useStores } from 'mobx/useStores';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { FC } from 'react';
import styled from 'styled-components';
import ElasticAutocomplete from './ElasticAutocomplete';
import SearchHeroCarousel, { CarouselImage } from './SearchHeroCarousel';
import { HeroWrapper, SearchWrapper, StyledBlock } from './styles';
import { observer } from 'mobx-react-lite';
import { ShopNavigationOptions } from '@data/shops';

export interface ElasticSearchHeroProps {
  header: string;
  locale: string;
  propertyImages?: CarouselImage[];
  pageData?: any;
  googleSearchCountryCodeList?: string[];
  shopNavigationOptions: ShopNavigationOptions;
  hideRentFilters?: boolean;
  shopGroupId?: string;
}

// PARENT COMPONENT
const ElasticSearchHero: FC<ElasticSearchHeroProps> = observer(
  ({
    locale,
    propertyImages,
    pageData,
    googleSearchCountryCodeList,
    shopNavigationOptions,
    hideRentFilters,
  }) => {
    const t = useTranslations();
    const router = useRouter();
    const { shopStore } = useStores();
    const { shopIds } = shopStore;

    const handleSaveSearchLog = async (prediction: Prediction) => {
      const normalizedSearch: Partial<Prediction> = prediction;
      delete normalizedSearch['count'];
      await apiClient.saveFrequentlySearchedArea({
        saveFrequentlySearchedAreaDto: {
          searchTerm: JSON.stringify(normalizedSearch),
          locationType: prediction.type as {},
          shopOrShopGroupId: shopIds ? shopIds[0] : 'americas',
        },
      });
    };

    const handleSearch = async (prediction: Prediction, tabIndex: number) => {
      if (
        prediction.type !== PredictionTypeEnum.Address &&
        prediction.type !== PredictionTypeEnum.PostalCode
      ) {
        await handleSaveSearchLog(prediction);
      }

      if (prediction.listingUrl) {
        router.push(prediction.listingUrl);
        return;
      }

      const listingTypes = tabIndex === 0 ? 'For Sale' : 'For Rent';
      const query: Record<string, string> = {
        ...prediction.addressComponents,
        location: prediction.text,
        listingTypes,
      };

      if (shopIds) {
        query.shopIds = shopIds.join(',');
      }

      if (prediction.type === PredictionTypeEnum.Neighborhood) {
        const neighborhoodId = prediction.addressComponents.localLogicId;
        if (neighborhoodId) {
          query.neighborhoodId = neighborhoodId;
        } else {
          query.neighborhood = prediction.addressComponents.neighborhood || '';
        }
        const country = prediction.addressComponents.country;
        if (country) {
          query.country = country;
        }
      }

      const searchQuery = new URLSearchParams(query).toString();
      router.push(`/properties${searchQuery ? `?${searchQuery}` : ''}`);
    };

    const hideBuyRentTab =
      hideRentFilters ||
      shopNavigationOptions?.hideRent ||
      shopNavigationOptions?.hideBuy ||
      shopNavigationOptions?.hideProperties;

    const hideSearchBar = !!pageData?.fields?.hideSearchBar;

    return (
      <ElasticSearchHeroContainer>
        <StyledBlock fullOnMobile>
          <HeroWrapper>
            {!hideSearchBar && (
              <SearchWrapper>
                <ElasticAutocomplete
                  id="property-search-input"
                  locale={locale}
                  placeholder={
                    pageData?.fields?.searchHeroCarousel?.fields?.searchPlaceholderText
                      ? pageData?.fields?.searchHeroCarousel?.fields?.searchPlaceholderText
                      : t('propertySearchPlaceholder')
                  }
                  showTabs={!hideBuyRentTab}
                  countries={googleSearchCountryCodeList}
                  evListingsOnly={false}
                  onPredictionClick={handleSearch}
                />
              </SearchWrapper>
            )}

            {propertyImages && propertyImages.length > 0 ? (
              <SearchHeroCarousel propertyImages={propertyImages} />
            ) : (
              // TODO: make more robust placeholder system
              <AspectBox $ratio={[1, 2 / 1, 2 / 1]}>
                <EVImg
                  src="/assets/textimageplaceholder.png"
                  alt="Search Hero Background Generic Image"
                  dataTestId="search-hero-image"
                />
              </AspectBox>
            )}
          </HeroWrapper>
        </StyledBlock>
      </ElasticSearchHeroContainer>
    );
  },
);

export default ElasticSearchHero;

const ElasticSearchHeroContainer = styled.div`
  @media (min-width: 375px) {
    width: 100vw;
    margin-left: calc(-50vw + 50%);
    padding: unset;
    box-sizing: border-box;
  }
  @media (${query.atLeast('tablet')}) {
    padding: unset;
  }
  @media (${query.atLeast('max')}), (${query.atLeast('desktop')}) {
    width: unset;
    margin-left: unset;
    padding: unset;
  }
`;
